import {
  Box, Heading, Text, VStack, FormControl, FormLabel,
  Input, Button, useToast, Spinner, IconButton, Image
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { buscarCotacaoPorToken, enviarRespostasCotacao } from "../services/api";

const formatarMoeda = (valor) => {
  if (!valor) return "";
  const numero = valor.toString().replace(/\D/g, "");
  const valorNumerico = parseFloat(numero) / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valorNumerico);
};

const extrairValorNumerico = (valorFormatado) => {
  if (!valorFormatado) return "";
  return valorFormatado.replace(/\D/g, "");
};

const formatarParaEnvio = (valorFormatado) => {
  if (!valorFormatado) return "";
  const numero = valorFormatado.replace(/\D/g, "");
  return (parseFloat(numero) / 100).toFixed(2);
};

export default function ResponderCotacao() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const fornecedorNome = searchParams.get("fornecedor") || "Desconhecido";
  const fornecedorWhatsapp = searchParams.get("whatsapp") || null;

  const [loading, setLoading] = useState(true);
  const [cotacao, setCotacao] = useState(null);
  const [respostas, setRespostas] = useState([]);
  const [naoPossuo, setNaoPossuo] = useState({});
  const [formEnviado, setFormEnviado] = useState(false); // <- novo estado
  const toast = useToast();

  useEffect(() => {
    const fetchCotacao = async () => {
      try {
        const data = await buscarCotacaoPorToken(token);
        setCotacao(data);
        setRespostas(data.itens.map((item) => ({
          cotacao_item_id: item.id,
          opcoes: [{ marca: "", preco: "", quantidade_disponivel: "" }],
        })));
      } catch {
        toast({
          title: "Erro ao buscar cotação",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCotacao();
  }, [token, toast]);

  const handleChange = (itemIndex, opcaoIndex, field, value) => {
    const updated = [...respostas];
    if (field === "quantidade_disponivel") {
      value = value.replace(/\D/g, "");
    } else if (field === "preco") {
      value = formatarMoeda(value);
    }
    updated[itemIndex].opcoes[opcaoIndex][field] = value;
    setRespostas(updated);
  };

  const addOpcao = (itemIndex) => {
    const updated = [...respostas];
    updated[itemIndex].opcoes.push({ marca: "", preco: "", quantidade_disponivel: "" });
    setRespostas(updated);
  };

  const removeOpcao = (itemIndex, opcaoIndex) => {
    const updated = [...respostas];
    updated[itemIndex].opcoes.splice(opcaoIndex, 1);
    setRespostas(updated);
  };

  const toggleNaoPossuo = (itemIndex) => {
    const updated = { ...naoPossuo, [itemIndex]: !naoPossuo[itemIndex] };
    setNaoPossuo(updated);
    if (!naoPossuo[itemIndex]) {
      toast({
        title: "Item marcado como 'Não possuo'",
        description: "Este item será considerado como não disponível.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const enviarResposta = async () => {
    try {
      const body = {
        cotacao_token: token,
        fornecedor_nome: fornecedorNome,
        fornecedor_whatsapp: fornecedorWhatsapp,
        respostas: respostas.map((item) => {
          const opcoesValidadas = item.opcoes
            .filter(op => op.marca && op.preco)
            .map(op => ({
              marca: op.marca,
              preco: formatarParaEnvio(op.preco),
              quantidade_disponivel: op.quantidade_disponivel,
              possui_item: true
            }));
          return {
            cotacao_item_id: item.cotacao_item_id,
            opcoes: opcoesValidadas.length > 0 ? opcoesValidadas : [{ possui_item: false }]
          };
        }),
      };
      await enviarRespostasCotacao(body);
      toast({
        title: "Resposta enviada com sucesso!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setFormEnviado(true); // <- formulário escondido após envio
    } catch (err) {
      toast({
        title: "Erro ao enviar resposta",
        description: err.response?.data?.message || "Verifique os dados e tente novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Carregando cotação...</Text>
      </Box>
    );
  }

  return (
    <Box maxW="900px" mx="auto" p={6}>
      <Image
        src="/banner_responder_cotacao.png"
        alt="Banner responder cotação"
        w="100%"
        maxH="250px"
        objectFit="cover"
        borderRadius="md"
        mb={6}
      />

      {formEnviado ? (
        <Box textAlign="center" mt={10}>
          <Heading size="md" mb={4}>Obrigado por enviar sua resposta!</Heading>
          <Text>Entraremos em contato se necessário.</Text>
        </Box>
      ) : (
        <>
          <Heading mb={4}>Responder Cotação</Heading>
          <VStack spacing={8} align="stretch">
            {cotacao.itens.map((item, itemIndex) => (
              <Box
                key={item.id}
                p={5}
                borderWidth="1px"
                borderRadius="md"
                bg={naoPossuo[itemIndex] ? "red.100" : "gray.50"}
              >
                <Text fontWeight="bold" fontSize="lg">{item.nome_produto}</Text>
                <Text color="gray.600">Marca desejada: {item.marca_desejada}</Text>
                <Text color="gray.600">Codigo Global: {item.codigo_global}</Text>
                <Text color="gray.600" mb={4}>Quantidade solicitada: {item.quantidade}</Text>

                {respostas[itemIndex]?.opcoes.map((opcao, opcaoIndex) => (
                  <Box key={opcaoIndex} borderWidth="1px" borderRadius="md" p={4} mt={3} bg="white">
                    <FormControl mb={2}>
                      <FormLabel>Marca ofertada</FormLabel>
                      <Input
                        value={opcao.marca}
                        onChange={(e) => handleChange(itemIndex, opcaoIndex, "marca", e.target.value)}
                      />
                    </FormControl>
                    <FormControl mb={2}>
                      <FormLabel>Preço (R$)</FormLabel>
                      <Input
                        type="text"
                        value={opcao.preco}
                        onChange={(e) => handleChange(itemIndex, opcaoIndex, "preco", e.target.value)}
                        placeholder="R$ 0,00"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Quantidade disponível (opcional)</FormLabel>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={opcao.quantidade_disponivel}
                        onChange={(e) =>
                          handleChange(itemIndex, opcaoIndex, "quantidade_disponivel", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </FormControl>
                    <Box mt={4} display="flex" alignItems="center" justifyContent="space-between">
                      <FormControl display="flex" alignItems="center">
                        <input
                          type="checkbox"
                          id={`nao-possuo-${itemIndex}`}
                          style={{ marginRight: "8px" }}
                          checked={!!naoPossuo[itemIndex]}
                          onChange={() => toggleNaoPossuo(itemIndex)}
                        />
                        <FormLabel htmlFor={`nao-possuo-${itemIndex}`} mb="0" fontWeight="normal">
                          Não possuo esse item
                        </FormLabel>
                      </FormControl>
                      {respostas[itemIndex].opcoes.length > 1 && opcaoIndex > 0 && (
                        <IconButton
                          size="sm"
                          colorScheme="red"
                          icon={<DeleteIcon />}
                          onClick={() => removeOpcao(itemIndex, opcaoIndex)}
                          aria-label="Remover opção"
                        />
                      )}
                    </Box>
                  </Box>
                ))}
                <Button
                  mt={4}
                  size="sm"
                  leftIcon={<AddIcon />}
                  onClick={() => addOpcao(itemIndex)}
                  colorScheme="blue"
                  variant="outline"
                >
                  Adicionar Opção
                </Button>
              </Box>
            ))}
            <Button colorScheme="green" onClick={enviarResposta}>
              Enviar Respostas
            </Button>
          </VStack>
        </>
      )}
    </Box>
  );
}
